import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Building2,
  ArrowLeft,
  Calendar,
  RefreshCw,
  AlertCircle,
  Info,
} from 'lucide-react';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import { Badge } from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import { api } from '../lib/api';
import { Case } from '../types';

// Import visualization components from standalone company detail
import QuarterlyFilingsTable from '../components/QuarterlyFilingsTable';
// TODO: Uncomment and refactor when these components are updated to accept proper data
// import FinancialFlowSankey from '../components/visualizations/FinancialFlowSankey';
// import IncomeStatementTrends from '../components/visualizations/IncomeStatementTrends';
// import FinancialTimeline from '../components/visualizations/FinancialTimeline';
// import BalanceSheetTrends from '../components/visualizations/BalanceSheetTrends';
// import FinancialRatios from '../components/visualizations/FinancialRatios';
// import ValuationMetrics from '../components/visualizations/ValuationMetrics';
// import BalanceSheetMetrics from '../components/visualizations/BalanceSheetMetrics';
// import CashFlowMetrics from '../components/visualizations/CashFlowMetrics';
// import FinancialRatiosMetrics from '../components/visualizations/FinancialRatiosMetrics';
// import LiquidityHealthCard from '../components/visualizations/LiquidityHealthCard';
// import WorkingCapitalBreakdown from '../components/visualizations/WorkingCapitalBreakdown';
// import CashFlowWaterfall from '../components/visualizations/CashFlowWaterfall';
// import OperatingExpenseBreakdown from '../components/visualizations/OperatingExpenseBreakdown';
// import CapitalAllocationDashboard from '../components/visualizations/CapitalAllocationDashboard';
// import BalanceSheetDetailTable from '../components/visualizations/BalanceSheetDetailTable';

type TabType = 'overview' | 'financials' | 'segments' | 'reports';

interface Project {
  caseId: string;
  name: string;
  description: string;
  status: string;
  companyId?: string;
  companyName?: string;
  snapshotVersion?: string;
  hasNewData?: boolean;
  createdAt: string;
  createdBy: string;
}

interface Company {
  companyId: string;
  companyName: string;
  ticker?: string;
  cik?: string;
  source: string;
  reportCount: number;
  lastResearchedAt: string;
  latestRevenue?: string;
  latestPeriod?: string;
}

export default function CaseClientDetails() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [showRefreshModal, setShowRefreshModal] = useState(false);

  // Fetch project to get company reference and snapshot metadata
  const { data: project, isLoading: isLoadingCase } = useQuery<Case>({
    queryKey: ['case', caseId],
    queryFn: async () => {
      const response = await api.get(`/cases/${caseId}`);
      return response;
    },
    enabled: !!caseId,
  });

  // Fetch company data using companyId from project
  const { data: company, isLoading: isLoadingCompany } = useQuery<Company>({
    queryKey: ['company', project?.companyId],
    queryFn: async () => {
      const response = await api.get(`/companies/${project?.companyId}`);
      return response;
    },
    enabled: !!project?.companyId,
  });

  // Refresh company snapshot mutation
  const refreshMutation = useMutation({
    mutationFn: async () => {
      return await api.post(`/cases/${caseId}/refresh-company-snapshot`);
    },
    onSuccess: (data) => {
      // Invalidate queries to refetch with updated data
      queryClient.invalidateQueries({ queryKey: ['case', caseId] });
      toast.success('Company data snapshot updated successfully');
      setShowRefreshModal(false);
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to refresh company data');
    },
  });

  const handleRefreshSnapshot = () => {
    refreshMutation.mutate();
  };

  if (isLoadingCase) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-neutral-600">Case not found</p>
      </div>
    );
  }

  if (!project.companyId) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <p className="text-neutral-900 font-medium mb-2">No Company Data</p>
        <p className="text-neutral-600">This project does not have a company associated with it.</p>
      </div>
    );
  }

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: Building2 },
    { id: 'financials' as TabType, label: 'Financial Analysis', icon: Calendar },
    { id: 'segments' as TabType, label: 'Business Segments', icon: Building2 },
    { id: 'reports' as TabType, label: 'SEC Reports', icon: Calendar },
  ];

  return (
    <div>
      {/* Header with Back Button */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(`/cases/${caseId}/summary`)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Project
        </Button>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-[40px] font-bold text-neutral-900 mb-2">Client Details</h1>
            <p className="text-neutral-600">{project.name}</p>
          </div>
        </div>
      </div>

      {/* Snapshot Version Alert */}
      {project.snapshotVersion && (
        <Card className="mb-6 border-l-4 border-l-blue-500">
          <CardBody>
            <div className="flex items-start gap-4">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-neutral-900 mb-1">Data Snapshot Information</p>
                <p className="text-sm text-neutral-600">
                  This data was captured on{' '}
                  <span className="font-medium">
                    {new Date(project.snapshotVersion).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  . This represents a point-in-time snapshot for consistent project analysis.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* New Data Available Alert */}
      {project.hasNewData && (
        <Card className="mb-6 border-l-4 border-l-amber-500 bg-amber-50">
          <CardBody>
            <div className="flex items-start gap-4">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-neutral-900 mb-1">New Data Available</p>
                <p className="text-sm text-neutral-600 mb-3">
                  Company data has been updated since this snapshot was captured. You can refresh to get the latest information.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowRefreshModal(true)}
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh Company Data
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Company Header Card */}
      {isLoadingCompany ? (
        <div className="flex items-center justify-center h-[200px]">
          <Spinner size="lg" />
        </div>
      ) : company ? (
        <Card className="mb-6">
          <CardBody>
            <div className="flex items-start gap-6">
              <div className="p-4 bg-primary-100 rounded-lg">
                <Building2 className="w-8 h-8 text-primary-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-neutral-900 mb-2">{company.companyName}</h2>
                    <div className="flex items-center gap-4 text-sm text-neutral-600">
                      {company.ticker && (
                        <Badge variant="secondary">
                          {company.ticker}
                        </Badge>
                      )}
                      {company.cik && <span>CIK: {company.cik}</span>}
                      <span>•</span>
                      <span>{company.reportCount} reports</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/companies/${company.companyId}`, '_blank')}
                  >
                    View Full Details
                  </Button>
                </div>

                {company.latestRevenue && (
                  <div className="mt-4 pt-4 border-t border-neutral-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-neutral-600 mb-1">Latest Revenue</p>
                        <p className="text-lg font-semibold text-neutral-900">{company.latestRevenue}</p>
                      </div>
                      <div>
                        <p className="text-sm text-neutral-600 mb-1">Period</p>
                        <p className="text-lg font-semibold text-neutral-900">{company.latestPeriod || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {/* Tabs */}
      <div className="border-b border-neutral-200 mb-6">
        <div className="flex gap-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600 font-medium'
                    : 'border-transparent text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content - Reuse visualization components */}
      {company && (
        <div>
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* TODO: These components need data objects, not just companyId.
                  Refactor to fetch and pass actual financial data like CompanyDetail.tsx does. */}
              <Card>
                <CardBody>
                  <div className="text-center py-12">
                    <Info className="mx-auto h-12 w-12 text-neutral-400 mb-4" />
                    <p className="text-neutral-600">Financial overview visualization components need refactoring</p>
                    <p className="text-sm text-neutral-500 mt-2">These components require financial data objects instead of just company ID</p>
                  </div>
                </CardBody>
              </Card>
              {/*
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <BalanceSheetMetrics companyId={company.companyId} />
                <CashFlowMetrics companyId={company.companyId} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <LiquidityHealthCard companyId={company.companyId} />
                <FinancialRatiosMetrics companyId={company.companyId} />
              </div>
              */}
            </div>
          )}

          {activeTab === 'financials' && (
            <div className="space-y-6">
              {/* TODO: These components need data objects (reports array, financial snapshots), not just companyId.
                  Refactor to fetch financial reports and pass data like CompanyDetail.tsx does. */}
              <Card>
                <CardBody>
                  <div className="text-center py-12">
                    <Info className="mx-auto h-12 w-12 text-neutral-400 mb-4" />
                    <p className="text-neutral-600">Financial visualization components need refactoring</p>
                    <p className="text-sm text-neutral-500 mt-2">These components require reports data objects instead of just company ID</p>
                  </div>
                </CardBody>
              </Card>
              {/*
              <FinancialTimeline companyId={company.companyId} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <IncomeStatementTrends companyId={company.companyId} />
                <BalanceSheetTrends companyId={company.companyId} />
              </div>
              <WorkingCapitalBreakdown companyId={company.companyId} />
              <CashFlowWaterfall companyId={company.companyId} />
              <OperatingExpenseBreakdown companyId={company.companyId} />
              <CapitalAllocationDashboard companyId={company.companyId} />
              <BalanceSheetDetailTable companyId={company.companyId} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <FinancialRatios companyId={company.companyId} />
                <ValuationMetrics companyId={company.companyId} />
              </div>
              <FinancialFlowSankey companyId={company.companyId} />
              */}
            </div>
          )}

          {activeTab === 'segments' && (
            <div>
              <p className="text-neutral-600 text-center py-12">
                Business segment analysis will be displayed here
              </p>
            </div>
          )}

          {activeTab === 'reports' && (
            <div>
              <QuarterlyFilingsTable
                companyId={company.companyId}
                companyName={company.companyName}
                cik={company.cik || ''}
              />
            </div>
          )}
        </div>
      )}

      {/* Refresh Confirmation Modal */}
      <Modal
        isOpen={showRefreshModal}
        onClose={() => setShowRefreshModal(false)}
        title="Refresh Company Data"
        size="md"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setShowRefreshModal(false)}
              disabled={refreshMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRefreshSnapshot}
              disabled={refreshMutation.isPending}
            >
              {refreshMutation.isPending && (
                <RefreshCw className="w-4 h-4 animate-spin" />
              )}
              {refreshMutation.isPending ? 'Refreshing...' : 'Refresh Snapshot'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-neutral-700">
            This will update the project's company data snapshot to the current version.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">What happens when you refresh:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>The snapshot timestamp will be updated to now</li>
                  <li>The "New Data Available" alert will be dismissed</li>
                  <li>You'll be working with the latest company information</li>
                </ul>
              </div>
            </div>
          </div>

          {project?.snapshotVersion && (
            <div className="text-sm text-neutral-600">
              <p>
                <span className="font-medium">Current snapshot:</span>{' '}
                {new Date(project.snapshotVersion).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
